import { useState } from 'react';
import { DataTableState } from 'src/shared/components/data-table/dataTableTypes';
import { useAppSelector } from 'src/store';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import useAuthService from 'src/shared/hooks/useAuthService';
import { getUserManagementListFilterState } from 'src/app/admin/views/user-management/state/userManagementSelector';
import {
    useGetCustomerUsersForSuperAdminQuery,
    useGetCustomerUsersQuery,
} from 'src/app/admin/views/user-management/state/api/userManagementGraphSlice';
import { checkIfCustomerAdmin } from 'src/app/admin/views/user-management/userManagementHelpers';
import { User } from 'src/shared/types';
export const USER_MANAGEMENT_LIST_ROWS_PER_PAGE = 15;

const useUserManagementList = () => {
    const { currentCustomerId } = useCustomer();
    const { isCurrentUserOnboardingManager, isCurrentUserSuperAdmin } = useAuthService();
    const filters = useAppSelector(getUserManagementListFilterState);
    const [tableState, setTableState] = useState<Partial<DataTableState>>({
        fixedHeader: true,
        hiddenColumnIds: new Set(['userId']),
        rowsPerPage: USER_MANAGEMENT_LIST_ROWS_PER_PAGE,
    });

    const { isLoading: isFetchingUsersForSuperAdmin, currentData: currentDataForSuperAdmin } = useGetCustomerUsersForSuperAdminQuery(
        {
            filters: {
                customerId: currentCustomerId,
                emailQuery: filters.userEmailSearchText,
                userType: isCurrentUserOnboardingManager ? 'external' : null,
            },
            pagination: {
                page: filters.currentPageNumber || 1,
                pageSize: tableState.rowsPerPage as number,
            },
        },
        {
            skip: !(isCurrentUserOnboardingManager || isCurrentUserSuperAdmin),
            refetchOnMountOrArgChange: true,
        }
    );

    const { isLoading: isFetchingUsersForCustomerAdmin, currentData: currentDataForCustomerAdmin } = useGetCustomerUsersQuery(
        {
            filters: {
                customerId: currentCustomerId,
                emailQuery: filters.userEmailSearchText,
            },
            pagination: {
                page: filters.currentPageNumber || 1,
                pageSize: tableState.rowsPerPage as number,
            },
        },
        {
            skip: isCurrentUserOnboardingManager || isCurrentUserSuperAdmin,
            refetchOnMountOrArgChange: true,
        }
    );

    const getSortedUsers = (users: User[]) => {
        return [...users].sort((val1, val2) => {
            const isVal1CustomerAdmin = checkIfCustomerAdmin(val1.customers, currentCustomerId);
            const isVal2CustomerAdmin = checkIfCustomerAdmin(val2.customers, currentCustomerId);
            if (isVal1CustomerAdmin && !isVal2CustomerAdmin) {
                return -1;
            } else if (!isVal1CustomerAdmin && isVal2CustomerAdmin) {
                return 1;
            } else {
                return val1.id - val2.id;
            }
        });
    };

    return {
        isFetchingUsersForCustomerAdmin,
        currentDataForCustomerAdmin,
        sortedUsersForCustomerAdmin: getSortedUsers(currentDataForCustomerAdmin?.users || []),
        isFetchingUsersForSuperAdmin,
        currentDataForSuperAdmin,
        sortedUsersForSuperAdmin: getSortedUsers(currentDataForSuperAdmin?.users || []),
        tableState,
        setTableState,
    };
};

export default useUserManagementList;
